import { useEffect, useState } from "react";
import {
  getPreconDraft,
  getPreconReport,
} from "../../../../services/projectService";
import {
  ApiProject,
  PreconDraftPayload,
  PreconFormValues,
} from "../types/precon.types";
import { normalizePreconReport, emptyInitialValues } from "../utils/normalize";
import { normalizeDraft } from "../utils/payload";

interface UsePreconReportResult {
  initialValues: PreconFormValues;
  prefilledFields: Set<keyof PreconFormValues>;
  loading: boolean;
  error: string;
}

type ApiResponse<T> = { data: T | null; error: { message?: string } | null };

// Any top-level key that the backend uses for draft sections.
// Checking ANY match means partial drafts and missing sections still resolve.
const DRAFT_KEYS: ReadonlyArray<keyof PreconDraftPayload> = [
  "projectSetup",
  "certification",
  "executiveSummary",
  "contractDelivery",
  "allowancesContingencies",
  "budgetAnalysis",
  "scheduleClosingActions",
  "deliverySettings",
];

function isDraftPayload(obj: unknown): obj is PreconDraftPayload {
  if (!obj || typeof obj !== "object") return false;
  return DRAFT_KEYS.some((k) => k in (obj as object));
}

// Handles all three nesting patterns seen across backends:
//   { data: { projectSetup: ... } }          — standard wrapper
//   { data: { data: { projectSetup: ... } } } — double-wrapped (some frameworks)
//   { projectSetup: ... }                     — payload returned directly
function resolveDraftPayload(body: unknown): PreconDraftPayload | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;

  if (isDraftPayload(b.data)) return b.data as PreconDraftPayload;

  if (b.data && typeof b.data === "object") {
    const inner = (b.data as Record<string, unknown>).data;
    if (isDraftPayload(inner)) return inner as PreconDraftPayload;
  }

  if (isDraftPayload(b)) return b as unknown as PreconDraftPayload;

  return null;
}

const usePreconReport = (projectId: string): UsePreconReportResult => {
  const [initialValues, setInitialValues] =
    useState<PreconFormValues>(emptyInitialValues);
  const [prefilledFields, setPrefilledFields] = useState<
    Set<keyof PreconFormValues>
  >(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError("");

      // Fetch both in parallel — project API always provides prefilledFields
      const [draftRes, projectRes] = await Promise.all([
        getPreconDraft(projectId) as Promise<ApiResponse<unknown>>,
        getPreconReport(projectId) as Promise<
          ApiResponse<{ data?: ApiProject }>
        >,
      ]);

      if (cancelled) return;

      // prefilledFields always come from the project API so they survive draft reloads
      const project = !projectRes.error
        ? ((projectRes.data as any)?.data as ApiProject | undefined)
        : undefined;
      const pf = project
        ? normalizePreconReport(project).prefilledFields
        : new Set<keyof PreconFormValues>();

      // 1. Use draft for form values if available
      if (!draftRes.error) {
        const draft = resolveDraftPayload(draftRes.data);
        if (draft) {
          const draftValues = normalizeDraft(draft);
          if (project) {
            const projectValues = normalizePreconReport(project).initialValues;
            // Checklist-derived fields always use live project data —
            // users cannot edit these so they are never saved into drafts
            if (!draft.contractDelivery?.yesNoItems?.length) {
              draftValues.yesNoItems = projectValues.yesNoItems;
            }
            draftValues.primaryDescriptionPoints =
              projectValues.primaryDescriptionPoints ||
              draftValues.primaryDescriptionPoints;
            draftValues.totalBudget =
              projectValues.totalBudget || draftValues.totalBudget;
            draftValues.hardCostBudget =
              projectValues.hardCostBudget || draftValues.hardCostBudget;
            draftValues.mobilizationDate =
              projectValues.mobilizationDate || draftValues.mobilizationDate;
            draftValues.estCompletion =
              projectValues.estCompletion || draftValues.estCompletion;
            draftValues.borrowerEquityRequired =
              projectValues.borrowerEquityRequired;
            draftValues.borrowerEquityAmount =
              projectValues.borrowerEquityAmount ||
              draftValues.borrowerEquityAmount;
            draftValues.borrowerEquityDescription =
              projectValues.borrowerEquityDescription ||
              draftValues.borrowerEquityDescription;
          }
          setInitialValues(draftValues);
          setPrefilledFields(pf);
          setLoading(false);
          return;
        }
      }

      // 2. No draft — fall back to project API for form values too
      if (projectRes.error) {
        setError(projectRes.error.message ?? "Failed to load project.");
        setLoading(false);
        return;
      }

      if (project) {
        const { initialValues: iv } = normalizePreconReport(project);
        setInitialValues(iv);
        setPrefilledFields(pf);
      }

      setLoading(false);
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  return { initialValues, prefilledFields, loading, error };
};

export default usePreconReport;
